import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Layout } from '../../components/layout/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../../components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Loader2, Plus, Trash2, Edit3, GraduationCap, Star, Upload } from 'lucide-react';

const backend = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('session_token')}` });

const EMPTY = {
    title: '', provider_name: '', provider_logo_url: '',
    short_description: '', long_description: '',
    duration: '4 weeks', price: 'Contact for pricing', format: 'Online',
    category: '', tags: [], external_url: '',
    country: 'UK', is_featured: false, is_active: true, display_order: 0,
};

export default function AdminPracticalCoursesPage() {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEdit, setShowEdit] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editing, setEditing] = useState(null); // course_id or null (new)
    const [form, setForm] = useState({ ...EMPTY });
    const [tagsInput, setTagsInput] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${backend}/api/admin/practical-courses`, { headers: authHeaders() });
            const data = await res.json();
            setCourses(data.courses || []);
        } catch (e) {
            toast.error('Failed to load courses');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...EMPTY });
        setTagsInput('');
        setShowEdit(true);
    };

    const openEdit = (c) => {
        setEditing(c.course_id);
        setForm({ ...EMPTY, ...c });
        setTagsInput((c.tags || []).join(', '));
        setShowEdit(true);
    };

    const handleSave = async () => {
        if (!form.title.trim() || !form.provider_name.trim() || !form.short_description.trim() || !form.external_url.trim()) {
            toast.error('Title, provider, short description and external URL are required');
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form, tags: tagsInput.split(',').map((t) => t.trim()).filter(Boolean) };
            const url = editing ? `${backend}/api/admin/practical-courses/${editing}` : `${backend}/api/admin/practical-courses`;
            const method = editing ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
            const j = await res.json();
            if (!res.ok) throw new Error(j.detail || 'save failed');
            toast.success(editing ? 'Course updated' : 'Course created');
            setShowEdit(false);
            load();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this course?')) return;
        try {
            const res = await fetch(`${backend}/api/admin/practical-courses/${id}`, { method: 'DELETE', headers: authHeaders() });
            if (res.ok) { toast.success('Deleted'); load(); }
        } catch (e) { toast.error('Delete failed'); }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetch(`${backend}/api/admin/practical-courses/upload-logo`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` },
                body: fd,
            });
            const j = await res.json();
            if (!res.ok) throw new Error(j.detail || 'upload failed');
            setForm({ ...form, provider_logo_url: j.url });
            toast.success('Logo uploaded');
        } catch (err) { toast.error(err.message); }
        finally { setUploading(false); }
    };

    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-neutral-900 flex items-center gap-2"><GraduationCap className="w-8 h-8 text-primary-600" /> Practical Courses</h1>
                        <p className="text-neutral-600 mt-1">Manage curated training providers shown on /practical-courses.</p>
                    </div>
                    <Button onClick={openCreate} className="bg-primary-600 hover:bg-primary-700" data-testid="add-course-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add Course
                    </Button>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>Courses ({courses.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
                        ) : courses.length === 0 ? (
                            <div className="text-center py-10 text-neutral-500">No courses yet. Click "Add Course" to create one.</div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Featured</TableHead>
                                        <TableHead>Title</TableHead>
                                        <TableHead>Provider</TableHead>
                                        <TableHead>Category</TableHead>
                                        <TableHead>Country</TableHead>
                                        <TableHead>Active</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {courses.map((c) => (
                                        <TableRow key={c.course_id} data-testid={`course-row-${c.course_id}`}>
                                            <TableCell>{c.is_featured ? <Star className="w-4 h-4 text-amber-500 fill-amber-400" /> : '—'}</TableCell>
                                            <TableCell className="font-medium">{c.title}</TableCell>
                                            <TableCell>{c.provider_name}</TableCell>
                                            <TableCell>{c.category ? <Badge variant="secondary">{c.category}</Badge> : '—'}</TableCell>
                                            <TableCell>{c.country === 'Canada' ? '🇨🇦' : '🇬🇧'}</TableCell>
                                            <TableCell>{c.is_active ? <Badge className="bg-emerald-100 text-emerald-700">Yes</Badge> : <Badge className="bg-neutral-200 text-neutral-600">No</Badge>}</TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-course-${c.course_id}`}><Edit3 className="w-4 h-4" /></Button>
                                                    <Button size="sm" variant="outline" onClick={() => handleDelete(c.course_id)} className="text-rose-600 hover:bg-rose-50" data-testid={`delete-course-${c.course_id}`}><Trash2 className="w-4 h-4" /></Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                <Dialog open={showEdit} onOpenChange={setShowEdit}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="course-form-dialog">
                        <DialogHeader>
                            <DialogTitle>{editing ? 'Edit Course' : 'Add Course'}</DialogTitle>
                            <DialogDescription>Provider-side details displayed on the /practical-courses page.</DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-2">
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className="text-sm font-medium">Title *</label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-title-input" /></div>
                                <div><label className="text-sm font-medium">Provider name *</label><Input value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} data-testid="course-provider-input" /></div>
                            </div>
                            <div>
                                <label className="text-sm font-medium">Provider logo</label>
                                <div className="flex items-center gap-2 mt-1">
                                    {form.provider_logo_url && <img src={form.provider_logo_url.startsWith('/') ? `${backend}${form.provider_logo_url}` : form.provider_logo_url} alt="logo" className="h-12 w-auto max-w-[120px] object-contain rounded border" />}
                                    <label className="inline-flex items-center gap-2 bg-primary-50 text-primary-700 px-3 py-2 rounded-md cursor-pointer hover:bg-primary-100 text-sm">
                                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
                                        <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="course-logo-upload" />
                                    </label>
                                    <Input placeholder="or paste image URL" value={form.provider_logo_url || ''} onChange={(e) => setForm({ ...form, provider_logo_url: e.target.value })} className="flex-1" />
                                </div>
                            </div>
                            <div><label className="text-sm font-medium">Short description * (2-3 lines)</label><Textarea rows={3} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} data-testid="course-short-desc-input" /></div>
                            <div><label className="text-sm font-medium">Long description (rich HTML)</label><Textarea rows={6} value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} placeholder="&lt;p&gt;Detailed course description...&lt;/p&gt;" data-testid="course-long-desc-input" /></div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div><label className="text-sm font-medium">Duration</label><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} /></div>
                                <div><label className="text-sm font-medium">Price</label><Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="course-price-input" /></div>
                                <div>
                                    <label className="text-sm font-medium">Format</label>
                                    <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Online">Online</SelectItem>
                                            <SelectItem value="In-person">In-person</SelectItem>
                                            <SelectItem value="Hybrid">Hybrid</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Country</label>
                                    <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="UK">🇬🇧 UK</SelectItem>
                                            <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className="text-sm font-medium">Category</label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Data Privacy, Governance, Tech..." /></div>
                                <div><label className="text-sm font-medium">Tags (comma-separated)</label><Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="GDPR, ISO 27001, DPO" data-testid="course-tags-input" /></div>
                            </div>
                            <div><label className="text-sm font-medium">External URL (CTA link) *</label><Input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://..." data-testid="course-url-input" /></div>

                            <div className="flex gap-6 items-center pt-2">
                                <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} data-testid="course-featured-checkbox" /> Featured on homepage</label>
                                <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowEdit(false)} disabled={saving}>Cancel</Button>
                            <Button onClick={handleSave} disabled={saving} className="bg-primary-600 hover:bg-primary-700" data-testid="save-course-btn">
                                {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving</> : (editing ? 'Save Changes' : 'Create Course')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
